import type { AnySceneObjectT } from "tweenly"
import { sortObjects } from "tweenly"
import { PayloadAction } from "@reduxjs/toolkit"
import { getAbsoluteTopLeftPosition } from "../../../../helpers/object.helpers"
import { canDrop, DropPlacementT } from "../../../../helpers/objectsTreeDrag.helpers"
import type { DropPositionT } from "../../../../helpers/objectsTreeDropPosition.helpers"
import { ObjectsStateT } from "../../objects.slice"
import { checkIndexesBase } from "./checkIndexesBase"

export type MoveObjectsPayloadT = {
    /** the dragged objects, in the order they land in */
    ids: string[]
    targetId: string
    placement: DropPlacementT
    /**
     * top / left and transforms under the new parent, measured on the canvas
     * (measureDropPositions) — an object missing here falls back to summing up the parents'
     * top / left, and keeps its transforms
     */
    positions?: Record<string, DropPositionT>
}

/**
 * Drop objects from the objects tree before, after or inside another one — in ONE write, so
 * the whole drop is one undo step.
 *
 * Every moved object keeps its place on the canvas: one that changes parent gets the top /
 * left measured for it before the drop — or, failing that, re-summed from the new parent the
 * way "move selected here" / "move to root" do it. Its matrix makes up for the new parents'
 * transforms, so that it looks exactly as it did.
 * The new parent's children (or the root objects) are then numbered in their new order,
 * `childIds` included, and every parent that lost a child is numbered again too.
 */
export const moveObjectsBaseAction = (
    state: ObjectsStateT,
    action: PayloadAction<MoveObjectsPayloadT>
) => {
    const { ids, targetId, placement, positions = {} } = action.payload
    if (!canDrop(state.value, ids, targetId, placement)) return

    const target = state.value[targetId]
    const parentId = placement === "inside" ? target.id : target.parentId
    const parent = parentId ? state.value[parentId] : undefined

    // the siblings the objects land among, in tree order, without the objects themselves
    const siblings: AnySceneObjectT[] = parent
        ? parent.childIds.map((id) => state.value[id]).filter(Boolean)
        : sortObjects(Object.values(state.value).filter((object) => object.parentId === null))
    const remaining = siblings.filter((sibling) => !ids.includes(sibling.id))
    const position =
        placement === "inside"
            ? remaining.length
            : remaining.findIndex((sibling) => sibling.id === targetId) +
              (placement === "after" ? 1 : 0)

    // measured before anything moves: positions are summed up the parent chain
    const parentPosition = parent ? getAbsoluteTopLeftPosition(state, parent) : { top: 0, left: 0 }
    const moves = ids
        .map((id) => state.value[id])
        .filter(Boolean)
        .map((object) => ({ object, absolute: getAbsoluteTopLeftPosition(state, object) }))

    const previousParentIds = new Set<string | null>()
    moves.forEach(({ object, absolute }) => {
        if (object.parentId === parentId) return
        const { left, top, transforms } = positions[object.id] ?? {
            left: Number(absolute.left) - Number(parentPosition.left),
            top: Number(absolute.top) - Number(parentPosition.top),
            transforms: object.transforms,
        }
        previousParentIds.add(object.parentId)
        if (object.parentId) {
            const previous = state.value[object.parentId]
            state.value[object.parentId] = {
                ...previous,
                childIds: previous.childIds.filter((id) => id !== object.id),
            }
        }
        state.value[object.id] = {
            ...state.value[object.id],
            parentId,
            transforms,
            // a flex child has no top / left of its own: nothing to carry over then
            styles: state.value[object.id].styles.map((style) =>
                style.property === "left"
                    ? { ...style, value: left }
                    : style.property === "top"
                    ? { ...style, value: top }
                    : style
            ),
        }
    })

    const order = [
        ...remaining.slice(0, position).map((sibling) => sibling.id),
        ...moves.map(({ object }) => object.id),
        ...remaining.slice(position).map((sibling) => sibling.id),
    ]
    order.forEach((id, index) => {
        if (state.value[id].index !== index) state.value[id] = { ...state.value[id], index }
    })
    if (parentId) {
        state.value[parentId] = {
            ...state.value[parentId],
            childIds: order,
            // dropped inside a collapsed branch: open it, so what was dropped is in view
            isExpanded: placement === "inside" ? true : state.value[parentId].isExpanded,
        }
    }

    previousParentIds.forEach((previousParentId) => checkIndexesBase(state, previousParentId))
}
